import React, { useEffect, useRef, useState } from 'react';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { useCatalog } from '../state/hooks';
import { ClassInsightsContext } from './ClassInsightsContext';
import { useFilters, statsBody } from './useFilters';
import { useDebounce } from '../../hooks/useDebounce';
import { defaultClassFor } from './catalogSelectors';
import { SUCCESS_CRITERIA_DEFAULT_SORT } from './utils';
import { Header } from './Header';
import { Tiles } from './Tiles';
import { KpiSummaryCards } from './KpiSummaryCards';
import { StudentsPopup } from './StudentsPopup';
import { CommentBanksPanel } from './CommentBanksPanel';
import { SuccessCriteriaPanel } from './SuccessCriteriaPanel';
import { RubricsPanel } from './RubricsPanel';
import { ShortAnswerPanel } from './ShortAnswerPanel';
import Loader from '../Loader';
import '../Analytics/index.css';

Chart.register(ArcElement, Tooltip, Legend);

const seedDefaultClass = (catalog, filters, setFilters) => {
  const defaultClass = defaultClassFor(catalog);
  if (defaultClass && filters.classIds.length === 0) {
    setFilters({ classIds: [String(defaultClass.id)] });
    return true;
  }
  return false;
};

export default function ClassInsightsPage() {
  const { filters, setFilters } = useFilters();
  const debouncedFilters = useDebounce(filters, 400);
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [expandedOpen, setExpandedOpen] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  const [justSeeded, setJustSeeded] = useState(false);
  // The small tile and the expanded modal are two separate SuccessCriteriaPanel
  // instances, so panel-local sort state cannot survive the expand. Hold it here,
  // above both, and hand it down through context.
  const [successCriteriaSortBy, setSuccessCriteriaSortBy] = useState(
    SUCCESS_CRITERIA_DEFAULT_SORT
  );
  const hasSeededDefaultClass = useRef(false);
  const { data: catalog, isLoadingdata, isFetching } = useCatalog(statsBody(debouncedFilters));

 
  useEffect(() => {
    window.__classInsightsContext__ = {
      filters,
      setFilters,
      catalog,
      expandPanel: (panelId, isExport = false) => {
        setExportMode(isExport);
        setExpandedPanel(panelId);
        setExpandedOpen(true);
      },
      collapsePanel: () => {
        setExpandedOpen(false);
      },
    };
    return () => {
      delete window.__classInsightsContext__;
    };
  }, [filters, setFilters, catalog]);

  useEffect(() => {
    if (hasSeededDefaultClass.current) return;
    const hasClassIdsInUrl = new URLSearchParams(window.location.search).getAll('classIds').length > 0;
    if (hasClassIdsInUrl) return;
    if (seedDefaultClass(catalog, filters, setFilters)) {
      hasSeededDefaultClass.current = true;
      setJustSeeded(true);
    }
  }, [catalog, filters, setFilters]);

  useEffect(() => {
    if (justSeeded && !isFetching) {
      setJustSeeded(false);
    }
  }, [justSeeded, isFetching]);

  const handleExpand = (panelId) => {
    setExpandedPanel(panelId);
    setExpandedOpen(true);
  };
  const handleCollapse = () => {
    setExpandedOpen(false);
  };
  const handleExpandedExit = () => {
    if (!expandedOpen) {
      setExpandedPanel(null);
    }
  };

  if (isLoadingdata && !catalog) return <Loader />;
  if (justSeeded) return <Loader />;

  return (
    <ClassInsightsContext.Provider
      value={{
        filters,
        debouncedFilters,
        setFilters,
        catalog,
        successCriteriaSortBy,
        setSuccessCriteriaSortBy,
      }}
    >
      <div className="ci-page class-insights-page">
        <Header />
        <KpiSummaryCards />
        <Tiles
          onExpandCommentBanks={() => handleExpand('comment-banks')}
          onExpandSuccessCriteria={() => handleExpand('success-criteria')}
          onExpandRubrics={() => handleExpand('rubrics')}
          onExpandShortAnswer={() => handleExpand('short-answer')}
        />
        {expandedPanel === 'comment-banks' && (
          <CommentBanksPanel
            mode="expanded"
            onCollapse={handleCollapse}
            open={expandedOpen}
            onExited={handleExpandedExit}
            exportMode={exportMode}
          />
        )}
        {expandedPanel === 'success-criteria' && (
          <SuccessCriteriaPanel
            mode="expanded"
            onCollapse={handleCollapse}
            open={expandedOpen}
            onExited={handleExpandedExit}
            exportMode={exportMode}
          />
        )}
        {expandedPanel === 'rubrics' && (
          <RubricsPanel
            mode="expanded"
            onCollapse={handleCollapse}
            open={expandedOpen}
            onExited={handleExpandedExit}
            exportMode={exportMode}
          />
        )}
        {expandedPanel === 'short-answer' && (
          <ShortAnswerPanel
            mode="expanded"
            onCollapse={handleCollapse}
            open={expandedOpen}
            onExited={handleExpandedExit}
            exportMode={exportMode}
          />
        )}
        <StudentsPopup />
      </div>
    </ClassInsightsContext.Provider>
  );
}
